import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, Send, Zap, CheckCircle2, XCircle, Play, AlertTriangle, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { extrasApi, bl } from "@/lib/extrasApi";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const CHAT_SUGGESTIONS = {
    en: [
        "What industries do my hottest leads work in?",
        "How can I improve my reply rate above 10%?",
        "Which of my leads should I contact first?",
    ],
    fr: [
        "Dans quels secteurs sont mes leads les plus chauds ?",
        "Comment passer mon taux de réponse au-dessus de 10% ?",
        "Quels leads je dois contacter en priorité ?",
    ],
};

const AGENT_SUGGESTIONS = {
    en: [
        "Import 50 Guyane companies and run AI email guessing on all of them",
        "Find my top 20 leads with score above 70 and create a 3-step campaign for them",
        "Enrich my 10 highest-score leads with Hunter.io then queue them into Autopilot",
        "Give me a snapshot of my pipeline",
    ],
    fr: [
        "Importe 50 entreprises de Guyane et lance un guess d'email IA sur toutes",
        "Trouve mes 20 meilleurs leads (score > 70) et crée une campagne 3 étapes pour eux",
        "Enrichis mes 10 meilleurs leads avec Hunter.io puis mets-les dans l'Autopilote",
        "Donne-moi un snapshot de mon pipeline",
    ],
};

const TOOL_LABELS = {
    search_leads: { fr: "🔍 Rechercher des leads", en: "🔍 Search leads" },
    import_guyane: { fr: "📥 Importer Guyane (INSEE)", en: "📥 Import Guyane (INSEE)" },
    enrich_batch: { fr: "✨ Enrichir en batch", en: "✨ Enrich batch" },
    generate_campaign: { fr: "📝 Générer campagne IA", en: "📝 Generate AI campaign" },
    queue_autopilot: { fr: "🚀 Mettre en Autopilote", en: "🚀 Queue Autopilot" },
    stats_overview: { fr: "📊 Snapshot pipeline", en: "📊 Pipeline snapshot" },
};

function ToolBadge({ tool, locale }) {
    const l = TOOL_LABELS[tool] || {};
    return (
        <span className="inline-flex items-center px-2 py-1 rounded-md bg-brand-indigo/15 text-brand-indigo text-[11px] font-mono border border-brand-indigo/30">
            {l[locale] || tool}
        </span>
    );
}

export default function Copilot() {
    const { locale } = useI18n();
    const { refresh } = useAuth();
    const [mode, setMode] = useState("agent"); // "chat" | "agent"
    const [messages, setMessages] = useState([{
        role: "assistant",
        content: locale === "fr"
            ? "Salut. Je suis BOXEmate. Décris-moi ce que tu veux accomplir (ex. \"trouve-moi 30 CTOs SaaS et lance une campagne\") et je planifie + exécute pour toi. Ou passe en mode Chat pour une simple question."
            : "Hi. I'm BOXEmate. Tell me what you want to accomplish (e.g. \"find 30 SaaS CTOs and launch a campaign\") and I'll plan + execute for you. Or switch to Chat mode for a plain question.",
    }]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [plan, setPlan] = useState(null);
    const [executing, setExecuting] = useState(false);
    const bottom = useRef(null);

    useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, plan]);

    async function sendChat(msg) {
        const text = (msg ?? input).trim();
        if (!text) return;
        setMessages(m => [...m, { role: "user", content: text }]);
        setInput("");
        setSending(true);
        try {
            const res = await extrasApi.copilotChat(text);
            setMessages(m => [...m, { role: "assistant", content: res.reply }]);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setSending(false); }
    }

    async function generatePlan(msg) {
        const text = (msg ?? input).trim();
        if (!text) return;
        setMessages(m => [...m, { role: "user", content: text }]);
        setInput("");
        setSending(true);
        setPlan(null);
        try {
            const p = await extrasApi.boxemate.plan(text);
            setPlan(p);
            refresh();
            if (!p.steps || p.steps.length === 0) {
                setMessages(m => [...m, {
                    role: "assistant",
                    content: locale === "fr"
                        ? "❌ Je ne peux pas exécuter ça avec les outils disponibles. " + ((p.risk_notes || []).join(" · ") || "Reformule ?")
                        : "❌ Can't do this with available tools. " + ((p.risk_notes || []).join(" · ") || "Reword?"),
                }]);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setSending(false); }
    }

    async function executePlan() {
        if (!plan) return;
        setExecuting(true);
        try {
            const res = await extrasApi.boxemate.execute(plan);
            refresh();
            const okCount = res.results.filter(r => r.ok).length;
            const failCount = res.results.length - okCount;
            const summary = locale === "fr"
                ? `✅ ${okCount} étape(s) OK · ${failCount ? `⚠ ${failCount} en erreur` : ""} · Crédits restants: ${res.credits_remaining}`
                : `✅ ${okCount} step(s) OK · ${failCount ? `⚠ ${failCount} failed` : ""} · Credits left: ${res.credits_remaining}`;
            const detail = res.results.map(r => `  ${r.ok ? "✓" : "✗"} ${r.tool}: ${r.ok ? JSON.stringify(r.result).slice(0, 120) : r.error}`).join("\n");
            setMessages(m => [...m, {
                role: "assistant",
                content: `${summary}\n\n${detail}`,
            }]);
            setPlan(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Execution failed");
        } finally { setExecuting(false); }
    }

    function submit(e) {
        e.preventDefault();
        if (mode === "chat") sendChat();
        else generatePlan();
    }

    const suggestions = mode === "chat" ? CHAT_SUGGESTIONS[locale] : AGENT_SUGGESTIONS[locale];

    return (
        <div className="px-6 lg:px-10 py-6 flex flex-col h-[calc(100vh-64px)]">
            <div className="mb-4 flex items-start justify-between gap-4">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-indigo to-brand-violet flex items-center justify-center">
                        <Sparkles size={16} className="text-white" />
                    </div>
                    <div>
                        <h1 className="font-display font-black text-2xl tracking-tighter">
                            {mode === "agent" ? "BOXEmate" : bl("AI Copilot", "Copilote IA", locale)}
                        </h1>
                        <div className="text-xs text-zinc-500 font-mono">
                            GPT-5.2 · {mode === "agent"
                                ? bl("plans + executes for you (1c/plan + tool costs)", "planifie + exécute pour toi (1c/plan + coûts outils)", locale)
                                : bl("Q&A · 1 credit per message", "Q&R · 1 crédit par message", locale)}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-1 rounded-full bg-brand-surface border border-white/10 p-1">
                    <button
                        type="button"
                        onClick={() => { setMode("agent"); setPlan(null); }}
                        data-testid="copilot-mode-agent"
                        className={`px-3 py-1.5 text-xs font-medium rounded-full inline-flex items-center gap-1 transition ${
                            mode === "agent" ? "bg-brand-indigo text-white" : "text-zinc-400 hover:text-white"
                        }`}
                    >
                        <Zap size={12} /> {bl("Agent", "Agent", locale)}
                    </button>
                    <button
                        type="button"
                        onClick={() => { setMode("chat"); setPlan(null); }}
                        data-testid="copilot-mode-chat"
                        className={`px-3 py-1.5 text-xs font-medium rounded-full inline-flex items-center gap-1 transition ${
                            mode === "chat" ? "bg-brand-indigo text-white" : "text-zinc-400 hover:text-white"
                        }`}
                    >
                        <MessageSquare size={12} /> {bl("Chat", "Chat", locale)}
                    </button>
                </div>
            </div>

            <div className="flex-1 rounded-2xl bg-brand-surface border border-white/8 p-4 overflow-y-auto space-y-3">
                {messages.map((m, i) => (
                    <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"} data-testid={`copilot-msg-${i}`}>
                        <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-brand-indigo text-white" : "bg-brand-void border border-white/8 text-zinc-100"}`}>
                            <pre className="whitespace-pre-wrap font-sans">{m.content}</pre>
                        </div>
                    </div>
                ))}

                {sending && (
                    <div className="flex justify-start">
                        <div className="bg-brand-void border border-white/8 rounded-2xl px-4 py-3 flex items-center gap-2 text-xs text-zinc-400">
                            <Loader2 size={14} className="animate-spin text-brand-cyan" />
                            {mode === "agent" ? bl("Planning…", "Planification…", locale) : bl("Thinking…", "Réflexion…", locale)}
                        </div>
                    </div>
                )}

                {plan && plan.steps?.length > 0 && (
                    <div className="rounded-2xl border-2 border-brand-cyan/40 bg-brand-cyan/5 p-4" data-testid="copilot-plan">
                        <div className="flex items-center gap-2 mb-3">
                            <Zap size={14} className="text-brand-cyan" />
                            <div className="text-sm font-semibold text-brand-cyan">
                                {bl("Proposed plan", "Plan proposé", locale)}
                            </div>
                            <div className="ml-auto text-xs font-mono px-2 py-1 rounded bg-brand-cyan/15 text-brand-cyan">
                                {bl(`~${plan.total_est_credits}c estimated`, `~${plan.total_est_credits}c estimés`, locale)}
                            </div>
                        </div>
                        {plan.goal_summary && (
                            <p className="text-xs text-zinc-400 italic mb-3">{plan.goal_summary}</p>
                        )}
                        <div className="space-y-2">
                            {plan.steps.map((s, i) => (
                                <div key={i} className="flex items-start gap-3 p-2 rounded-lg bg-brand-void/60">
                                    <div className="w-6 h-6 rounded-md bg-brand-indigo/20 text-brand-indigo text-xs font-mono flex items-center justify-center shrink-0">{i + 1}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <ToolBadge tool={s.tool} locale={locale} />
                                            {s.est_credits > 0 && (
                                                <span className="text-[10px] font-mono text-amber-300">-{s.est_credits}c</span>
                                            )}
                                        </div>
                                        <div className="text-xs text-zinc-300">{s.why}</div>
                                        {Object.keys(s.args || {}).length > 0 && (
                                            <div className="text-[10px] font-mono text-zinc-500 mt-1 truncate">
                                                {JSON.stringify(s.args)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {plan.risk_notes?.length > 0 && (
                            <div className="mt-3 flex items-start gap-2 text-xs text-amber-300">
                                <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                                <div>{plan.risk_notes.join(" · ")}</div>
                            </div>
                        )}
                        <div className="mt-4 flex items-center gap-2">
                            <button
                                type="button"
                                onClick={executePlan}
                                disabled={executing}
                                data-testid="copilot-plan-execute"
                                className="flex-1 btn-cta text-white text-sm font-semibold px-4 py-2.5 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {executing ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
                                {bl(`Execute plan (${plan.total_est_credits}c)`, `Exécuter le plan (${plan.total_est_credits}c)`, locale)}
                            </button>
                            <button
                                type="button"
                                onClick={() => setPlan(null)}
                                disabled={executing}
                                data-testid="copilot-plan-cancel"
                                className="text-sm font-medium px-4 py-2.5 rounded-full border border-white/12 hover:border-rose-500/40 hover:text-rose-300 inline-flex items-center gap-2"
                            >
                                <XCircle size={13} /> {bl("Cancel", "Annuler", locale)}
                            </button>
                        </div>
                    </div>
                )}
                <div ref={bottom} />
            </div>

            {messages.length <= 1 && !plan && (
                <div className="mt-3 flex flex-wrap gap-2">
                    {suggestions.map((s, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => mode === "chat" ? sendChat(s) : generatePlan(s)}
                            data-testid={`copilot-suggest-${i}`}
                            className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:border-brand-cyan/50 hover:bg-brand-cyan/5 text-zinc-300 hover:text-white transition"
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}

            <form onSubmit={submit} className="mt-3 flex gap-2">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={mode === "agent"
                        ? bl("Tell me what to do (I'll plan then ask you to approve)…", "Dis-moi quoi faire (je planifie puis te fais valider)…", locale)
                        : bl("Ask anything about your pipeline…", "Pose-moi une question sur ton pipeline…", locale)}
                    data-testid="copilot-input"
                    disabled={!!plan || sending}
                    className="flex-1 px-4 py-3 rounded-full bg-brand-surface border border-white/10 focus:border-brand-indigo focus:ring-1 focus:ring-brand-indigo outline-none text-sm disabled:opacity-60"
                />
                <button
                    type="submit"
                    disabled={sending || !input.trim() || !!plan}
                    data-testid="copilot-send"
                    className="btn-cta text-white font-semibold px-5 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-60"
                >
                    {sending ? <Loader2 size={14} className="animate-spin" /> : mode === "agent" ? <Zap size={14} /> : <Send size={14} />}
                </button>
            </form>
        </div>
    );
}
